<template>
  <div class="subsea-cont">
    <h2 v-if="loading" class="loading">
      Loading data...
    </h2>
    <div class="fluid">
      <v-layout>
        <v-flex xs7>
          <RegionTimeChart
            ref="regionTimeChart"
            dimension-field="region"
            agg-field="capex"
            download-name="spend_by_region.csv"
            :ndx="$options.ndx"
            :interval="interval"
            @dimensionSelected="filterRegionTime"
          >
            <h2>Spend by Region (EPCI)</h2>
            <div class="sub">
              <strong class="sub">$millions</strong>
              <a @click="resetAll()" class="reset-all">Reset all</a>
            </div>
          </RegionTimeChart>

          <ChartForm :ndx="$options.ndx" :ndxLoaded="ndxLoaded" ref="form" @formChanged="reloadChart()" />

          <v-layout>
            <v-flex xs4>
              <PieChart
                ref="componentChart"
                dimension-field="component"
                agg-field="capex"
                :colors="$options.colors"
                :pie-attrs="$options.pieAttrs"
                download-name="spend_by_component.csv"
                :download-columns="['Component', '$millions']"
                :titleFn="labelFn"
                :labelFn="labelFn"
              >
                <h2>Spend by Component</h2>
                <div class="sub">
                  <strong class="sub">$millions ({{ minYear }} - {{ maxYear }})</strong>
                </div>
              </PieChart>
            </v-flex>
            <v-flex xs4>
              <PieChart
                ref="statusChart"
                dimension-field="status"
                agg-field="capex"
                :colors="$options.colors"
                :pie-attrs="$options.pieAttrs"
                download-name="spend_by_status.csv"
                :download-columns="['Status', '$millions']"
                :titleFn="labelFn"
                :labelFn="labelFn"
              >
                <h2>Spend by Status</h2>
                <div class="sub">
                  <strong class="sub">$millions ({{ minYear }} - {{ maxYear }})</strong>
                </div>
              </PieChart>
            </v-flex>
            <v-flex xs4></v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs5>
          <v-layout>
            <v-flex xs6>
              <RowChart
                ref="operatorsChart"
                :colors="$options.colors"
                dimension-field="operator"
                agg-field="capex"
                download-name="activity_by_E&P.csv"
                :download-columns="['Company', '$millions']"
                :titleFn="labelFn"
                :labelFn="labelFn"
              >
                <h2>Spend by E&P</h2>
                <div class="sub">
                  <strong class="sub">$millions</strong>
                </div>
              </RowChart>
            </v-flex>

            <v-flex xs6>
              <RowChart
                ref="countriesChart"
                dimension-field="country"
                agg-field="capex"
                download-name="spend_by_country.csv"
                :download-columns="['Country', '$millions']"
                :colors="$options.colors"
                :titleFn="labelFn"
                :labelFn="labelFn"
              >
                <h2>Spend by Country</h2>
                <div class="sub">
                  <strong class="sub">$millions</strong>
                </div>
              </RowChart>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import crossfilter from 'crossfilter2';
import '@node_modules/dc/dc.css';

import RegionTimeChart from '@/components/dcjs/RegionTime.vue';
import RowChart from '@/components/dcjs/RowChart.vue';
import PieChart from '@/components/dcjs/PieChart.vue';
import ChartForm from '@/components/dcjs/Form.vue';
import baseMixin from '@/components/dcjs/baseMixin';

export default {
  mixins: [baseMixin],
  years: [],
  originalData: [],
  components: {
    RegionTimeChart,
    PieChart,
    RowChart,
    ChartForm,
  },
  computed: {
    labelFn() {
      return d => {
        return `${d.key}: ${d3.format(',.1f')(d.value)}`;
      };
    },
  },
  //
  data() {
    return {
      comp: {},
      region: {},
      years: [],
      period: null,
      interval: null,
      loading: false,
      ndxLoaded: false,
    };
  },
  methods: {
    async $_loadData() {
      let data = [];
      this.loading = true;
      try {
        data = await d3.csv('/subsea-data.csv');
      } catch (err) {
        alert('There was an error fetching data from the server, please reload the page?');
        this.loading = false;
        return;
      }
      this.loading = false;
      this.$options.originalData = [...data];
      return data;
    },
    $_getData(data) {
      const minDate = new Date(this.$options.minDate);
      const maxDate = new Date(this.$options.maxDate);

      return data
        .map(d => {
          return {
            capex: +(d.capex || 0) / 1000,
            country: d.country || '',
            component: d._type,
            date: new Date(`${d[this.period]}T00:00:00`),
            operator: d.operator || '',
            region: d.region || '',
            status: d.status,
            water_depth: +(d.water_depth_m || 0),
          };
        })
        .filter(d => d.date != 'Invalid Date')
        .filter(d => d.date > minDate && d.date < maxDate);
    },
    //
    $_createDashboard(data) {
      this.years = [...new Set(data.map(d => d.date.getFullYear()))].sort();
      const ndx = crossfilter(data);
      this.$options.ndx = ndx;
      this.ndxLoaded = true;

      this.$refs.componentChart.createChart(ndx);
      this.$refs.statusChart.createChart(ndx);
      this.$refs.operatorsChart.createChart(ndx);
      this.$refs.countriesChart.createChart(ndx);
      this.$refs.regionTimeChart.createChart(ndx);

      dc.renderAll();
    },
    //
    $_selectStack(i) {
      return d => d.value[i];
    },
    //
    reset(chart) {
      this.charts[chart].filterAll();
      dc.redrawAll();
    },
  },
  async mounted() {
    const data = await this.$_loadData();
    this.period = this.$refs.form.form.period;
    this.interval = this.$refs.form.form.interval;
    this.$_createDashboard(this.$_getData(data));
  },
};
</script>
